import { CheckoutStatus } from "@brookbush/types";

export const CHECKOUT_QUERY_KEY = "checkout";
export const CHECKOUT_QUERY_VALUE = "1";

/** Membership page with the payment step requested. Survives a navigation. */
export const BECOME_A_MEMBER_CHECKOUT_HREF =
	`/become-a-member?${CHECKOUT_QUERY_KEY}=${CHECKOUT_QUERY_VALUE}` as const;

export type CheckoutQueryAction = "start" | "clear" | "strip-query" | "none";

/**
 * Whether the URL is asking to show the payment step.
 *
 * @param value - `checkout` query value from the router
 * @returns True when checkout should be open
 */
export const isCheckoutStepRequested = (
	value: string | string[] | undefined | null,
): boolean => {
	if (Array.isArray(value)) return value.includes(CHECKOUT_QUERY_VALUE);
	return value === CHECKOUT_QUERY_VALUE;
};

/**
 * Keep the URL and the in-memory checkout status from fighting.
 *
 * @param input.queryRequested - Checkout query is set
 * @param input.status - Current checkout status
 * @returns The one sync step to take, or none
 */
export const nextCheckoutQueryAction = (input: {
	queryRequested: boolean;
	status: CheckoutStatus;
}): CheckoutQueryAction => {
	if (input.status === CheckoutStatus.SUCCESSFUL && input.queryRequested) {
		return "strip-query";
	}
	if (input.queryRequested && input.status === CheckoutStatus.IDLE) {
		return "start";
	}
	if (!input.queryRequested && input.status === CheckoutStatus.IN_PROGRESS) {
		return "clear";
	}
	return "none";
};

/**
 * Drop the checkout flag and leave the rest of the query alone.
 *
 * @param query - Current route query
 * @returns Query without `checkout`
 */
export const omitCheckoutQuery = (
	query: Record<string, string | string[] | undefined>,
): Record<string, string | string[] | undefined> => {
	const next = { ...query };
	delete next[CHECKOUT_QUERY_KEY];
	return next;
};
